import React from 'react';

import { isDefined, isEmpty, silenceError } from '@nfctron/nexus-core';
import { useContextRequired, useFormat } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import { Controller, type UseFormReturn, useFieldArray } from 'react-hook-form';

import { Form } from '@root/features/form/components/Form/Form';
import { type FormSelectProps, type FormTextInputProps } from '@root/features/form/components/Form/Form.types';

import { ChipControl } from '@root/lib/components/ChipControl/ChipControl';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { countryEnumTickets } from '@root/lib/constants/country';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../components/CartContext';
import { type CartStoreCartedTicketsSlice, type CartStoreExtraProductsSlice, type CartStoreTicketTypesSlice } from '../stores/cartStore';

/** base field config type */
type BaseFieldConfig = {
	fieldId: string;
	title: React.ReactNode;
};

/** text field config type */
type TextFieldConfig = BaseFieldConfig & {
	fieldProps: Partial<Omit<FormTextInputProps, 'fieldId' | 'value' | 'defaultValue' | 'onChange'>>;
};

/** select field config type */
type SelectFieldConfig = BaseFieldConfig & {
	fieldProps: Partial<Omit<FormSelectProps, 'fieldId' | 'value' | 'defaultValue' | 'onChange'>>;
};

/** shipping address config type */
type ShippingAddressConfig = {
	address: TextFieldConfig;
	city: TextFieldConfig;
	postalCode: TextFieldConfig;
	country: SelectFieldConfig;
} | null;

/**
 * Event/Cart/ProductConfigurationForm atom component
 * @constructor
 */
export const ProductConfigurationForm: AtomComponent<ProductConfigurationFormProps> = ({ extraProductConfig, ticketTypeConfig, form }) => {
	const { event } = useContextRequired(EventCartPageContext);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductConfigurationForm.displayName);
	const { t: commonT } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const format = useFormat();

	/** use field array for chips array management */
	const { fields: chips } = useFieldArray({
		control: form.control,
		name: `chips`,
	});

	/** optionalText configurations */
	const optionalTextConfig = React.useMemo<{
		title: React.ReactNode;
		description: React.ReactNode;
		fieldProps: Partial<Omit<FormTextInputProps, 'fieldId' | 'value' | 'defaultValue' | 'onChange'>>;
	} | null>(() => {
		if (isEmpty(extraProductConfig.optionalTextRegex)) return null;

		switch (extraProductConfig.optionalTextRegex) {
			/** shipping-address for HradyCZ specific case (see below) */
			case 'shipping-address': {
				return null;
			}
			/** default case */
			default: {
				return {
					title: t('optionalText.label'),
					description: t('optionalText.description'),
					fieldProps: {
						placeholder: t('optionalText.placeholder'),
						options: {
							validate: (value) => {
								try {
									const regEx = new RegExp(extraProductConfig.optionalTextRegex!);
									if (!isEmpty(value) && !regEx.test(value as any)) return t('optionalText.invalidError.text');
									return undefined;
								} catch (e) {
									return undefined;
								}
							},
						},
					},
				};
			}
		}
	}, [extraProductConfig.optionalTextRegex, t]);

	/** specific cases (shipping-address for HradyCZ for example) */
	const shippingAddressConfig = React.useMemo<ShippingAddressConfig>(() => {
		if (isEmpty(extraProductConfig.optionalTextRegex)) return null;

		return {
			city: {
				fieldId: 'shippingAddress.city',
				title: t('optionalText.shippingAddress.city.label'),
				fieldProps: {
					placeholder: t('optionalText.shippingAddress.city.placeholder'),
					autoComplete: 'shipping address-level2',
					required: true,
					options: {
						validate: (value) => {
							if (isEmpty(value)) return t('optionalText.shippingAddress.city.requiredError.text');
							if (value.length < 3) return t('optionalText.shippingAddress.city.minError.text');
							return undefined;
						},
					},
				},
			},
			address: {
				fieldId: 'shippingAddress.address',
				title: t('optionalText.shippingAddress.address.label'),
				fieldProps: {
					placeholder: t('optionalText.shippingAddress.address.placeholder'),
					autoComplete: 'shipping address-level3',
					required: true,
					options: {
						validate: (value) => {
							if (isEmpty(value)) return t('optionalText.shippingAddress.address.requiredError.text');
							if (value.length < 3) return t('optionalText.shippingAddress.address.minError.text');
							return undefined;
						},
					},
				},
			},
			postalCode: {
				fieldId: 'shippingAddress.postalCode',
				title: t('optionalText.shippingAddress.postalCode.label'),
				fieldProps: {
					placeholder: t('optionalText.shippingAddress.postalCode.placeholder'),
					autoComplete: 'shipping zip-code',
					required: true,
					options: {
						validate: (value) => {
							if (isEmpty(value)) return t('optionalText.shippingAddress.postalCode.requiredError.text');
							if (value.length < 3) return t('optionalText.shippingAddress.postalCode.minError.text');
							return undefined;
						},
					},
				},
			},
			country: {
				fieldId: 'shippingAddress.country',
				title: t('optionalText.shippingAddress.country.label'),
				fieldProps: {
					placeholder: t('optionalText.shippingAddress.country.placeholder'),
					autoComplete: 'shipping country',
					required: true,
					options: {
						validate: (value) => {
							if (isEmpty(value)) return t('optionalText.shippingAddress.country.requiredError.text');
							return undefined;
						},
					},
				},
			},
		};
	}, [extraProductConfig.optionalTextRegex, t]);

	const countryOptions = countryEnumTickets.list
		.filter((country) => country.systemWideSupport)
		.map((country) => ({
			value: country.countryId.toString(),
			label: commonT(`constants.country.${country.countryId}.text`),
			icon: country.Icon ? <country.Icon /> : null,
			data: country,
		}));

	return (
		<React.Fragment>
			{/* optional text */}
			{isDefined(optionalTextConfig) && (
				<section>
					{/* title */}
					<Text c="gray" size="md" fw={700}>
						{optionalTextConfig.title}
					</Text>
					{/* text */}
					<Text size="xs" c="dimmed">
						{optionalTextConfig.description}
					</Text>
					{/* field */}
					<div className="mt-2">
						<Form.TextInput fieldId="optionalText" label={null} {...optionalTextConfig.fieldProps} />
					</div>
				</section>
			)}

			{/* shipping address (optional text special case) */}
			{isDefined(shippingAddressConfig) && (
				<section>
					{/* title */}
					<Text c="gray" size="md" fw={700}>
						{t('optionalText.shippingAddress.label')}
					</Text>
					{/* text */}
					<Text c="dimmed" className="text-base">
						{t('optionalText.shippingAddress.description')}
					</Text>
					{/* fields */}
					<div className="mt-2 flex flex-col gap-2">
						{/* address */}
						<Form.TextInput
							fieldId="optionalTextShippingAddress.address"
							label={shippingAddressConfig.address.title}
							{...shippingAddressConfig.address.fieldProps}
						/>
						{/* city */}
						<Form.TextInput
							fieldId="optionalTextShippingAddress.city"
							label={shippingAddressConfig.city.title}
							{...shippingAddressConfig.city.fieldProps}
						/>
						{/* postal code */}
						<Form.TextInput
							fieldId="optionalTextShippingAddress.postalCode"
							label={shippingAddressConfig.postalCode.title}
							{...shippingAddressConfig.postalCode.fieldProps}
						/>
						{/* country */}
						<Form.Select
							fieldId="optionalTextShippingAddress.country"
							label={shippingAddressConfig.country.title}
							data={countryOptions}
							transform={{
								input: (v) => {
									silenceError(() => {
										const country = countryEnumTickets.get(v, 'isoChar3');
										return country.countryId.toString();
									}, '');
								},
								output: (v) =>
									silenceError(() => {
										const country = countryEnumTickets.get(parseInt(v || '', 10));
										return country.isoChar3;
									}, null),
							}}
							{...shippingAddressConfig.country.fieldProps}
						/>
					</div>
				</section>
			)}

			{/* chips section */}
			{chips.length > 0 && (
				<section>
					{/* title */}
					<Text c="gray" size="md" fw={700}>
						{t('chips.label')}
					</Text>
					{/* text */}
					<Text size="xs" c="dimmed">
						{t('chips.description')}
					</Text>
					{/* chips */}
					<div className="mt-2 flex flex-col gap-3">
						{chips.map((chip, chipIndex) => (
							<Controller
								key={chip.id}
								control={form.control}
								name={`chips.${chipIndex}.amount`}
								rules={{
									validate: (value) => {
										if (value < ticketTypeConfig.creditMinimum && value > 0)
											return t('chips.minChargeError.text', {
												formattedAmount: format.price(ticketTypeConfig.creditMinimum, event.currency.baseCurrency.currencyId),
											});
									},
								}}
								render={(params) => (
									<ChipControl
										allowZeroCharge
										minCharge={ticketTypeConfig.creditMinimum}
										maxCharge={ticketTypeConfig.creditLimit}
										currency={event.currency}
										isValid={!isDefined(params.fieldState.error)}
										{...params.fieldState}
										error={params.fieldState.error?.message}
										{...params.field}
									/>
								)}
							/>
						))}
					</div>
				</section>
			)}
		</React.Fragment>
	);
};

/**
 * Event/Cart/ProductConfigurationForm atom props type
 * @export
 */
export type ProductConfigurationFormProps = {
	extraProductConfig: CartStoreExtraProductsSlice.ExtraProductConfig;
	ticketTypeConfig: CartStoreTicketTypesSlice.TicketTypeConfig;
	form: UseFormReturn<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>;
};

/**
 * Event/Cart/ProductConfigurationForm atom display name
 * @type {ComponentDisplayName<"atom">}
 */
ProductConfigurationForm.displayName = 'Event/Cart/ProductConfigurationFormAtom';
